import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

/**
 * Opens resources in the in-app viewer. The open resource lives in the URL
 * (`?resource=<s3 key>&title=<name>`), so the browser Back button closes the
 * viewer, the page underneath keeps its scroll position, and links can be shared.
 */

const RESOURCE_PARAM = "resource";
const TITLE_PARAM = "title";

interface ViewerState {
  /** Set when the viewer was opened by clicking inside the app. */
  openedInApp?: boolean;
}

export interface OpenResource {
  key: string;
  title: string;
}

export function useResourceViewer() {
  const history = useHistory<ViewerState | undefined>();
  const location = useLocation<ViewerState | undefined>();

  const current = useMemo<OpenResource | null>(() => {
    const params = new URLSearchParams(location.search);
    const key = params.get(RESOURCE_PARAM);
    return key ? { key, title: params.get(TITLE_PARAM) ?? "Resource" } : null;
  }, [location.search]);

  const open = useCallback(
    (key: string, title: string) => {
      const params = new URLSearchParams(location.search);
      params.set(RESOURCE_PARAM, key);
      params.set(TITLE_PARAM, title);
      history.push({
        pathname: location.pathname,
        search: params.toString(),
        state: { openedInApp: true },
      });
    },
    [history, location.pathname, location.search]
  );

  const close = useCallback(() => {
    if (location.state?.openedInApp) {
      // same as the browser Back button: returns to the exact previous page
      history.goBack();
    } else {
      // opened from a shared link: there's no in-app page to go back to
      const params = new URLSearchParams(location.search);
      params.delete(RESOURCE_PARAM);
      params.delete(TITLE_PARAM);
      history.replace({ pathname: location.pathname, search: params.toString() });
    }
  }, [history, location]);

  return { current, open, close };
}
